import { z } from 'zod';
import { projectArtifactSchema } from './project-artifact.js';
import { excludedProjectFileSchema } from './workspace-runtime.js';
/** Public Field identity. No Workshop attempt or published activity is manufactured. */
export const fieldWorkspaceContextSchema = z
    .object({
    version: z.literal(2),
    domain: z.literal('field'),
    workspaceId: z.uuid(),
    engagementId: z.uuid(),
    runtimeVersionId: z.uuid(),
    generation: z.number().int().positive(),
})
    .strict();
export const fieldWorkspaceOperationSchema = fieldWorkspaceContextSchema
    .extend({ operationId: z.uuid() })
    .strict();
export const fieldWorkspacePrepareSchema = fieldWorkspaceContextSchema
    .omit({ generation: true })
    .extend({
    operationId: z.uuid(),
    artifact: projectArtifactSchema,
    artifactHash: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
export const fieldWorkspaceCaptureSchema = fieldWorkspaceOperationSchema
    .extend({
    capturedAt: z.iso.datetime(),
    artifact: projectArtifactSchema,
    artifactHash: z.string().regex(/^[a-f0-9]{64}$/),
    excluded: z.array(excludedProjectFileSchema).max(500),
})
    .strict();
export const fieldWorkspaceEditorSchema = fieldWorkspaceContextSchema
    .extend({ sessionId: z.uuid(), replaceSessionId: z.uuid().optional() })
    .strict();
export const fieldWorkspaceCommandSchema = z
    .object({
    requestId: z.uuid(),
    action: z.enum(['activate', 'save', 'stop']),
    expectedGeneration: z.number().int().positive(),
    editorSessionId: z.uuid().nullable(),
    /**
     * `disk` lets a stop checkpoint the files already saved on disk when this tab holds no
     * connected Field editor (switching to Workshop from another tab); the worker refuses it
     * while an editor is still connected with its browser present. Saves always need the editor.
     */
    saveMode: z.enum(['editor', 'disk']).optional(),
    /** A stop that releases Field but keeps the compute running for the Workshop. */
    keepCompute: z.boolean().optional(),
})
    .strict()
    .superRefine((command, ctx) => {
    if (command.action !== 'stop' && (command.saveMode !== undefined || command.keepCompute))
        ctx.addIssue({
            code: 'custom',
            path: ['saveMode'],
            message: 'Only a stop may name a save mode or keep its compute',
        });
});
export const fieldWorkspaceJobSchema = z
    .object({
    id: z.uuid(),
    action: z.enum(['activate', 'save', 'stop']),
    status: z.enum(['pending', 'running', 'completed', 'failed']),
    error: z.string().nullable(),
    /** Started by the periodic backup or idle sweep, not by the learner; retried by the next sweep. */
    maintenance: z.boolean().optional(),
})
    .strict();
export const fieldWorkspaceStatusSchema = z
    .object({
    enabled: z.boolean(),
    workspaceId: z.uuid().nullable(),
    generation: z.number().int().positive(),
    state: z.string(),
    selection: z.enum(['none', 'workshop', 'field']),
    context: fieldWorkspaceContextSchema.nullable(),
    runtimeHash: z.string().nullable(),
    checkpointAt: z.iso.datetime().nullable(),
    operation: fieldWorkspaceJobSchema.nullable(),
    instructions: z.string(),
})
    .strict();
export const fieldWorkspaceLaunchSchema = z
    .object({
    sessionId: z.uuid(),
    expected: fieldWorkspaceContextSchema,
    recoverDisconnected: z.boolean().optional(),
})
    .strict();
export const fieldWorkspaceLaunchResultSchema = z
    .object({ action: z.url(), grant: z.string(), expiresAt: z.iso.datetime() })
    .strict();
export const fieldWorkspaceCaptureRequestSchema = z
    .object({
    expected: fieldWorkspaceContextSchema,
    editorSessionId: z.uuid(),
    operationId: z.uuid(),
    issuedAt: z.iso.datetime(),
})
    .strict();
